import { useFetch } from "../api/fetch/useFetch";
import { 
    VALIDATE_TELEGRAM_DATA_URL, 
    LOGIN_URL, 
    SET_CREDENTIALS_URL, 
    CHANGE_PASSWORD_URL, 
    ME_URL,
    RESTORE_PASSWORD_URL,
    VERIFY_RESTORE_CODE_URL,
    SET_RESTORED_PASSWORD_URL,
} from "../api/index";
import { createRepository } from "./Repository";

export interface AuthResponse {
    user: User;
    token: string;
}

export interface Request {
    initData: string;
}

export interface User {
    tg_id:     number;
    username:  string;
    take_test: boolean;
    balance:   number;
    has_credentials: boolean;
    login?: string | null;
}

export async function loginWithTelegram(initialData: string): Promise<AuthResponse> {
    const result = await useFetch<AuthResponse, Request>({
        method: 'POST',
        resource: VALIDATE_TELEGRAM_DATA_URL,
        body: { initData: initialData },
        adapter: (data) => data,
    });

    return result.data;
}

export interface LoginRequestPayload {
    login: string;
    password: string;
}

interface LoginResponsePayload {
    success: boolean;
    message: string;
    token?: string | null;
    user?: User | null;
}

export async function loginWithCredentials(payload: LoginRequestPayload): Promise<AuthResponse> {
    const result = await useFetch<LoginResponsePayload, LoginRequestPayload>({
        method: 'POST',
        resource: LOGIN_URL,
        body: payload,
        adapter: (data) => data,
    });

    const { success, token, user, message } = result.data;

    if (!success || !token || !user) {
        throw new Error(message || 'Не удалось авторизоваться');
    }

    return {
        token,
        user,
    };
}

export interface SetCredentialsPayload {
    login: string;
    password: string;
}

export interface SetCredentialsResponse {
    success: boolean;
    message: string;
}

export interface ChangePasswordPayload {
    old_password: string;
    new_password: string;
}

export interface ChangePasswordResponse {
    success: boolean;
    message: string;
}

// Восстановление пароля (шаг 1)
export interface RestorePasswordPayload {
    identifier: string;
}

export interface RestorePasswordResponse {
    success: boolean;
    message: string;
    masked_email?: string | null;
}

// Восстановление пароля (шаг 2)
export interface VerifyRestoreCodePayload {
    identifier: string;
    code: string;
}

export interface VerifyRestoreCodeResponse {
    success: boolean;
    message: string;
    reset_secret?: string | null;
}

// Восстановление пароля (шаг 3)
export interface SetRestoredPasswordPayload {
    identifier: string;
    reset_secret: string;
    new_password: string;
}

export interface SetRestoredPasswordResponse {
    success: boolean;
    message: string;
}

const authorizedAuthRepository = createRepository({
    basePath: '',
    useAuth: true,
});

export async function setCredentials(payload: SetCredentialsPayload) {
    const result = await authorizedAuthRepository.post<SetCredentialsResponse, SetCredentialsPayload>(
        SET_CREDENTIALS_URL,
        payload,
    );

    return result.data;
}

export async function changePassword(payload: ChangePasswordPayload) {
    const result = await authorizedAuthRepository.post<ChangePasswordResponse, ChangePasswordPayload>(
        CHANGE_PASSWORD_URL,
        payload,
    );

    return result.data;
}

export async function restorePassword(payload: RestorePasswordPayload): Promise<RestorePasswordResponse> {
    const result = await useFetch<RestorePasswordResponse, RestorePasswordPayload>({
        method: 'POST',
        resource: RESTORE_PASSWORD_URL,
        body: payload,
        adapter: (data) => data,
    });
    return result.data;
}

export async function verifyRestoreCode(payload: VerifyRestoreCodePayload): Promise<VerifyRestoreCodeResponse> {
    const result = await useFetch<VerifyRestoreCodeResponse, VerifyRestoreCodePayload>({
        method: 'POST',
        resource: VERIFY_RESTORE_CODE_URL,
        body: payload,
        adapter: (data) => data,
    });
    return result.data;
}

export async function setRestoredPassword(payload: SetRestoredPasswordPayload): Promise<SetRestoredPasswordResponse> {
    const result = await useFetch<SetRestoredPasswordResponse, SetRestoredPasswordPayload>({
        method: 'POST',
        resource: SET_RESTORED_PASSWORD_URL,
        body: payload,
        adapter: (data) => data,
    });
    return result.data;
}

export async function fetchProfile() {
    const result = await authorizedAuthRepository.get<User>(
        ME_URL,
    );

    return result.data;
}